import React, { useState, useEffect } from "react"
import {
    Box,
    Modal,
    Typography,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    CircularProgress,
    Grid,
} from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import { getWorkerSchedule } from "../../../utils/RosterAPI"
import { toast } from "react-toastify"

export default function WorkerScheduleModal({ open, onClose, worker, projectId }) {
    const [schedule, setSchedule] = useState(null)
    const [loading, setLoading] = useState(false)

    useEffect(() => {
        if (open && worker?.id) {
            fetchSchedule()
        } else {
            setSchedule(null)
        }
    }, [open, worker])

    const fetchSchedule = async () => {
        if (!worker?.id) return

        setLoading(true)
        try {
            const response = await getWorkerSchedule(worker.id, null, null, projectId)
            if (response) {
                setSchedule(response)
            }
        } catch (error) {
            console.error("Error fetching worker schedule:", error)
            toast.error(error?.message || "Failed to fetch worker schedule")
        } finally {
            setLoading(false)
        }
    }

    const formatDate = (dateStr) => {
        if (!dateStr) return ""
        const date = new Date(dateStr)
        const day = String(date.getDate()).padStart(2, "0")
        const month = String(date.getMonth() + 1).padStart(2, "0")
        const year = date.getFullYear()
        return `${day}/${month}/${year}`
    }

    const getAttendanceChip = (attendanceType) => {
        if (!attendanceType) return null

        const typeUpper = attendanceType.toUpperCase()
        let label = ""
        let bgColor = "#F2F4F7"
        let textColor = "#475467"

        switch (typeUpper) {
            case "FULL_DAY":
            case "FULLDAY": // Support old format if any
                label = "Full Day"
                bgColor = "#F2F4F7"
                textColor = "#475467"
                break
            case "HALF_DAY":
            case "HALFDAY": // Support old format if any
                label = "Half Day"
                bgColor = "#FFFAEB"
                textColor = "#B54708"
                break
            case "ABSENT":
                label = "Absent"
                bgColor = "#FEF3F2"
                textColor = "#D92D20"
                break
            default:
                return null
        }

        return (
            <Chip
                label={label}
                size="small"
                sx={{
                    backgroundColor: bgColor,
                    color: textColor,
                    fontWeight: 500,
                    fontSize: "12px",
                    textTransform: "capitalize",
                }}
            />
        )
    }

    if (!worker) return null

    return (
        <Modal
            open={open}
            onClose={onClose}
            sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
            }}
        >
            <Box
                sx={{
                    backgroundColor: "#fff",
                    borderRadius: "12px",
                    border: "1px solid #E4E7EC",
                    width: "90%",
                    maxWidth: "1000px",
                    maxHeight: "90vh",
                    overflowY: "auto",
                    boxShadow: "0px 8px 24px rgba(0,0,0,0.1)",
                }}
            >
                {/* Header */}
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        p: 3,
                        borderBottom: "1px solid #E4E7EC",
                        position: "sticky",
                        top: 0,
                        backgroundColor: "#fff",
                        zIndex: 1,
                    }}
                >
                    <Box>
                        <Typography sx={{ fontWeight: 600, fontSize: "20px", color: "#101828" }}>
                            {`${worker.firstName} ${worker.lastName}`}
                        </Typography>
                        <Typography sx={{ color: "#667085", fontSize: "14px", mt: 0.5 }}>
                            {worker.speciality} • {worker.phoneNumber}
                        </Typography>
                    </Box>
                    <IconButton
                        onClick={onClose}
                        sx={{ p: 0.5, backgroundColor: "#F4F3FF", borderRadius: "8px" }}
                    >
                        <CloseIcon sx={{ fontSize: 20, color: "#4D6BF6" }} />
                    </IconButton>
                </Box>

                {/* Content */}
                <Box sx={{ p: 3 }}>
                    {loading ? (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : schedule ? (
                        <>
                            {/* Worker Info & Summary */}
                            <Grid container spacing={3} sx={{ mb: 3 }}>
                                <Grid item xs={12} md={6}>
                                    <Paper
                                        elevation={0}
                                        sx={{
                                            p: 2,
                                            border: "1px solid #E4E7EC",
                                            borderRadius: "8px",
                                        }}
                                    >
                                        <Typography
                                            sx={{ fontWeight: 600, fontSize: "14px", color: "#101828", mb: 1 }}
                                        >
                                            Worker Information
                                        </Typography>
                                        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                                            <Typography sx={{ fontSize: "13px", color: "#667085" }}>
                                                <strong>National ID:</strong> {schedule.nationalId || "-"}
                                            </Typography>
                                            <Typography sx={{ fontSize: "13px", color: "#667085" }}>
                                                <strong>Nationality:</strong> {schedule.nationality || "-"}
                                            </Typography>
                                        </Box>
                                    </Paper>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Paper
                                        elevation={0}
                                        sx={{
                                            p: 2,
                                            border: "1px solid #E4E7EC",
                                            borderRadius: "8px",
                                        }}
                                    >
                                        <Typography
                                            sx={{ fontWeight: 600, fontSize: "14px", color: "#101828", mb: 1 }}
                                        >
                                            Summary
                                        </Typography>
                                        {schedule.summary ? (
                                            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                                                <Typography sx={{ fontSize: "13px", color: "#667085" }}>
                                                    <strong>Total Days Scheduled:</strong>{" "}
                                                    {schedule.summary.totalDaysScheduled || 0}
                                                </Typography>
                                                <Typography sx={{ fontSize: "13px", color: "#667085" }}>
                                                    <strong>Full Days:</strong> {schedule.summary.fullDays || 0}
                                                </Typography>
                                                <Typography sx={{ fontSize: "13px", color: "#667085" }}>
                                                    <strong>Half Days:</strong> {schedule.summary.halfDays || 0}
                                                </Typography>
                                                <Typography sx={{ fontSize: "13px", color: "#667085" }}>
                                                    <strong>Absent Days:</strong> {schedule.summary.absentDays || 0}
                                                </Typography>
                                            </Box>
                                        ) : (
                                            <Typography sx={{ fontSize: "13px", color: "#667085" }}>
                                                No summary available
                                            </Typography>
                                        )}
                                    </Paper>
                                </Grid>
                            </Grid>

                            {/* Projects */}
                            {schedule.projects && schedule.projects.length > 0 && (
                                <Box sx={{ mb: 3 }}>
                                    <Typography
                                        sx={{ fontWeight: 600, fontSize: "16px", color: "#101828", mb: 1 }}
                                    >
                                        Projects
                                    </Typography>
                                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                                        {schedule.projects.map((project, index) => (
                                            <Chip
                                                key={project.id || index}
                                                label={`${project.name} (${project.rosterName})`}
                                                sx={{
                                                    backgroundColor: "#F4F3FF",
                                                    color: "#4D6BF6",
                                                    fontWeight: 500,
                                                }}
                                            />
                                        ))}
                                    </Box>
                                </Box>
                            )}

                            {/* Schedule Table */}
                            <Typography sx={{ fontWeight: 600, fontSize: "16px", color: "#101828", mb: 2 }}>
                                Schedule
                            </Typography>
                            {schedule.schedule && schedule.schedule.length > 0 ? (
                                <TableContainer
                                    component={Paper}
                                    elevation={0}
                                    sx={{ border: "1px solid #E4E7EC", borderRadius: "8px" }}
                                >
                                    <Table>
                                        <TableHead sx={{ backgroundColor: "#F9FAFB" }}>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 600, fontSize: "14px", color: "#101828" }}>
                                                    Date
                                                </TableCell>
                                                <TableCell sx={{ fontWeight: 600, fontSize: "14px", color: "#101828" }}>
                                                    Project
                                                </TableCell>
                                                <TableCell sx={{ fontWeight: 600, fontSize: "14px", color: "#101828" }}>
                                                    Week Start
                                                </TableCell>
                                                <TableCell sx={{ fontWeight: 600, fontSize: "14px", color: "#101828" }}>
                                                    Attendance
                                                </TableCell>
                                                <TableCell sx={{ fontWeight: 600, fontSize: "14px", color: "#101828" }}>
                                                    Remarks
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {schedule.schedule.map((item, index) => (
                                                <TableRow key={index}>
                                                    <TableCell sx={{ fontSize: "14px", color: "#101828" }}>
                                                        {formatDate(item.date)}
                                                    </TableCell>
                                                    <TableCell sx={{ fontSize: "14px", color: "#101828" }}>
                                                        {item.projectName || "-"}
                                                    </TableCell>
                                                    <TableCell sx={{ fontSize: "14px", color: "#101828" }}>
                                                        {formatDate(item.weekStartDate)}
                                                    </TableCell>
                                                    <TableCell>{getAttendanceChip(item.attendanceType)}</TableCell>
                                                    <TableCell sx={{ fontSize: "14px", color: "#667085" }}>
                                                        {item.remarks || "-"}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            ) : (
                                <Box sx={{ py: 4, textAlign: "center" }}>
                                    <Typography sx={{ color: "#667085", fontSize: "14px" }}>
                                        No schedule records found
                                    </Typography>
                                </Box>
                            )}
                        </>
                    ) : (
                        <Box sx={{ py: 4, textAlign: "center" }}>
                            <Typography sx={{ color: "#667085", fontSize: "14px" }}>
                                No schedule data available
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Box>
        </Modal>
    )
}

